import { IS_AWS_ENV } from "constants/app.constants";

interface ApiParts {
  readonly api10: string;
  readonly county: string;
  readonly serial: string;
  readonly permitId: Maybe<string>;
}

// Copied from Insights' `state_link` (app-etl wells_combined.sql), keyed by API
// state code; Texas is moved to https.
const URL_BY_STATE_CODE: Record<string, (api: ApiParts) => Maybe<string>> = {
  "03": () =>
    "https://aogc.state.ar.us/DocuWare/PlatformRO/WebClient/Integration?&ep=La3C1fn4U2i9-ZD6SVhPB0KCUSiAsIrGO_OASkaFBDQYVJ7_t69XA15GehzbXhHfwKFAvSrzALIWXjBV9bxGv8fZsySvdc06sIbU0niLNrcOcanpisCvbPvFie1eLKiFlyR8PYf03uWXhMJIero4pEgUbtexHyEcsdNYVZnLphrGGFjZ4girNr-19UE2-GnM0",
  "04": ({ county, serial }) =>
    `https://filerequest.conservation.ca.gov/WellRecord?api=${county}${serial}`,
  "05": ({ county, serial }) =>
    `https://ecmc.state.co.us/cogisdb/Facility/FacilityDetail?api=${county}${serial}`,
  "15": () => "https://www.kcc.ks.gov/oil-gas/data-search",
  "17": ({ permitId }) =>
    permitId
      ? `https://ucmwww.dnr.state.la.us/ucmsearch/FindDocuments.aspx?idx=xwellserialnumber&val=${encodeURIComponent(
          permitId
        )}`
      : null,
  "25": ({ api10 }) =>
    `https://bogapps.dnrc.mt.gov/Dataminer/Wells/WellData.aspx?Name=${api10}0000`,
  "30": ({ county, serial }) =>
    `https://wwwapps.emnrd.nm.gov/ocd/ocdpermitting/Data/WellDetails.aspx?api=30-${county}-${serial}`,
  "33": ({ permitId }) =>
    permitId
      ? `https://www.dmr.nd.gov/oilgas/feeservices/getscoutticket.asp?FileNumber=${encodeURIComponent(
          permitId
        )}`
      : null,
  "34": ({ api10 }) =>
    `https://gis.ohiodnr.gov/MapViewer/WellSummaryCard.asp?api=${api10}0000`,
  "35": ({ api10 }) =>
    `https://public.occ.ok.gov/OGCDWebLink/Search.aspx?searchcommand=%7B%5BOG%20Well%20Records%5D:%5BAPI%20Number%5D=%22${api10}*%22%7D`,
  "37": () =>
    "https://www.dep.pa.gov/DataandTools/Reports/Oil%20and%20Gas%20Reports/Pages/default.aspx",
  "42": ({ county, serial }) =>
    `https://webapps.rrc.texas.gov/DP/publicQuerySearchAction.do?countyCode=${county}&apiSeqNo=${serial}`,
  "43": () =>
    "https://oilgas.ogm.utah.gov/oilgasweb/live-data-search/lds-files/files-lu.xhtml",
  "47": ({ api10 }) => `https://apps.dep.wv.gov/oog/wellsearch/wellApi.cfm?api=${api10}`,
  // Wyoming's napino drops the county's leading digit.
  "49": ({ county, serial }) =>
    `https://pipeline.wyo.gov/Wellapino.cfm?napino=${county.slice(1)}${serial}&s1=Y`
};

// Read directly rather than through `Uwi`, whose parser takes a leading 5 as a
// prefix and misreads Alaska (50) and Hawaii (51) keys as other states.
const US_API = /^\d{10}(\d{2,4})?$/;

export interface StateFilings {
  /** Null when the well is a US well but no page can be built for it. */
  readonly href: Maybe<string>;
}

/**
 * The state regulator's page for a US well, on AWS only. Null for any other
 * well. `permitId` is the state permit number Louisiana and North Dakota key on.
 */
export function stateFilings(
  uwi: Maybe<string>,
  permitId: Maybe<string>
): Maybe<StateFilings> {
  if (!IS_AWS_ENV || !uwi || !US_API.test(uwi)) return null;
  const api10 = uwi.slice(0, 10);
  const parts = { api10, county: api10.slice(2, 5), serial: api10.slice(5), permitId };
  return { href: URL_BY_STATE_CODE[api10.slice(0, 2)]?.(parts) ?? null };
}
